/**
 * Zod schema + form/DTO mapping for the approval chain creator/editor.
 *
 * Single source of truth for the approval chain builder's shape, validation rules,
 * threshold-option metadata, defaults, and the form -> Create/Update DTO
 * mappers. The form (`ApprovalChainForm`) and its host panel
 * (`ApprovalChainEditorPanel`) both drive off this module so the validation
 * contract lives in exactly one place.
 *
 * Validation contract (from the approval design spec +):
 *   - Approval chain name required.
 *   - 1-6 steps.
 *   - Each step: name required, >= 1 approver: a person or a project role.
 *   - A step that names a role cannot require all approvers.
 *   - When threshold is AT_LEAST_N, the count must be an integer >= 1. Without a role it must
 *     also be <= the number of people, or it surfaces the spec error:
 *       "Step {n} requires at least {N} approvers but only {M} users are assigned"
 *     With a role, the role's holders count too (`approvalChainFormSchemaFor`).
 */
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import type {
  UpdateApprovalChainInput,
  ApprovalChain,
  ApprovalChainStep,
} from '@/entities/approval-chain';
import { i18n } from '@/shared/i18n';

/** Max number of steps an approval chain may contain (backend enforces the same). */
export const MAX_STEPS = 6;

/** Approval threshold discriminant, matching the generated DTO enum. */
export type ApprovalThresholdType = 'at_least_one' | 'at_least_n' | 'all';

/**
 * Whether steps must clear in position order, or may clear in any order. The builder offers
 * this as a single "run steps in order" checkbox, so unticked is the default and means
 * `any_order` — the reverse of the API's own default, which is what an omitted field means.
 */
export type ChainExecutionMode = 'in_order' | 'any_order';

/** Ordered threshold options for the per-step selector. */
export const THRESHOLD_OPTIONS: readonly {
  value: ApprovalThresholdType;
  /** Resolved by the rendering component so the label follows the active locale. */
  labelKey:
    | 'manageApprovalChains.schema.atLeastOneApprover'
    | 'manageApprovalChains.schema.atLeastNApprovers'
    | 'manageApprovalChains.schema.allApprovers';
  /** Whether this option exposes the numeric approver count input. */
  requiresCount: boolean;
}[] = [
  {
    value: 'at_least_one',
    labelKey: 'manageApprovalChains.schema.atLeastOneApprover',
    requiresCount: false,
  },
  {
    value: 'at_least_n',
    labelKey: 'manageApprovalChains.schema.atLeastNApprovers',
    requiresCount: true,
  },
  { value: 'all', labelKey: 'manageApprovalChains.schema.allApprovers', requiresCount: false },
];

/**
 * A single assignee as held in form state. We keep display fields alongside the
 * id so the picker can render chips without a second lookup; only `userId` is
 * sent to the API.
 */
export interface ApprovalChainAssigneeOption {
  userId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
}

/** The project role a step names, held with its name so the chip renders without a lookup. */
export interface ApprovalChainStepRoleOption {
  id: string;
  name: string;
}

const approverRoleSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
});

const assigneeSchema = z.object({
  userId: z.string().min(1),
  name: z.string(),
  email: z.string(),
  avatarUrl: z.string().nullable(),
});

const stepSchema = z.object({
  /** Stable client-side key for React lists + dnd (never sent to the API). */
  tempId: z.string(),
  stepId: z.string().optional(),
  name: z
    .string()
    .trim()
    .min(1, {
      error: () => i18n.t('approvalChains:manageApprovalChains.schema.stepNameIsRequired'),
    }),
  approvalThresholdType: z.enum(['at_least_one', 'at_least_n', 'all']),
  /**
   * Only meaningful when `approvalThresholdType === 'at_least_n'`. Kept as a
   * number so the numeric input round-trips cleanly; cross-checked against the
   * assignee count in the form-level superRefine below.
   */
  approvalThresholdCount: z.number().int().min(1).optional(),
  assignees: z.array(assigneeSchema),
  approverRole: approverRoleSchema.nullable(),
});

export const approvalChainFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, {
        error: () =>
          i18n.t('approvalChains:manageApprovalChains.schema.approvalChainNameIsRequired'),
      }),
    executionMode: z.enum(['in_order', 'any_order']),
    autoApplyToNewFolders: z.boolean(),
    /** One-time action on save; never persisted as approval chain state. */
    autoApplyToExistingFolders: z.boolean(),
    steps: z
      .array(stepSchema)
      .min(1, {
        error: () => i18n.t('approvalChains:manageApprovalChains.schema.addAtLeastOneStep'),
      })
      .max(MAX_STEPS, {
        error: () =>
          i18n.t('approvalChains:manageApprovalChains.schema.anApprovalChainCanHaveAt', {
            max: MAX_STEPS,
          }),
      }),
  })
  .superRefine((form, ctx) => {
    form.steps.forEach((step, index) => {
      if (step.assignees.length === 0 && !step.approverRole) {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'assignees'],
          message: i18n.t('approvalChains:manageApprovalChains.schema.addAtLeastOneApprover'),
        });
      }

      if (step.approverRole && step.approvalThresholdType === 'all') {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'approvalThresholdType'],
          message: i18n.t('approvalChains:manageApprovalChains.schema.allApproversNotWithRole'),
        });
      }

      if (step.approvalThresholdType !== 'at_least_n') {
        return;
      }

      const count = step.approvalThresholdCount;
      if (count === undefined || Number.isNaN(count)) {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'approvalThresholdCount'],
          message: i18n.t('approvalChains:manageApprovalChains.schema.enterTheNumberOfRequired'),
        });
        return;
      }

      const assigneeCount = step.assignees.length;
      if (!step.approverRole && count > assigneeCount) {
        ctx.addIssue({
          code: 'custom',
          path: ['steps', index, 'approvalThresholdCount'],
          message: i18n.t(
            'approvalChains:manageApprovalChains.schema.stepRequiresAtLeastApprovers',
            {
              step: index + 1,
              required: count,
              assigned: assigneeCount,
            },
          ),
        });
      }
    });
  });

/** What the editor knows beyond the form's own values, for the checks the server would make. */
export interface ApprovalChainFormContext {
  /** The chain's steps as last saved, by step id; empty for a chain not saved yet. */
  savedSteps: ReadonlyMap<string, StepInput>;
  /** Who holds each project role, by identity id; `null` while that is unknown. */
  roleHolders: ReadonlyMap<string, ReadonlySet<string>> | null;
}

type StepInput = ApprovalChainFormInput['steps'][number];

/**
 * The form schema plus the refusal the server would make, so it shows on the step instead of in a
 * toast after saving: the people named and the role's holders together fall short of the
 * threshold, as a saved step does once holders leave the role.
 */
export function approvalChainFormSchemaFor(context: ApprovalChainFormContext) {
  return approvalChainFormSchema.superRefine((form, ctx) => {
    form.steps.forEach((step, index) => {
      const shortfall = roleApproverShortfall(step, index, context.roleHolders);
      if (shortfall) ctx.addIssue({ code: 'custom', ...shortfall });
    });
  });
}

interface RoleStepCapacity {
  required: number;
  available: number;
}

/**
 * How many approvals a role step needs and how many people could give them, counting someone
 * both named and holding the role once. `null` for a step without a role, or when who holds it
 * is unknown.
 */
function roleStepCapacity(
  step: StepInput,
  roleHolders: ApprovalChainFormContext['roleHolders'],
): RoleStepCapacity | null {
  const role = step.approverRole;
  if (!role || !roleHolders || step.approvalThresholdType === 'all') return null;

  const required = step.approvalThresholdType === 'at_least_n' ? step.approvalThresholdCount : 1;
  if (required === undefined) return null;
  const holders = roleHolders.get(role.id) ?? new Set<string>();
  const available = new Set([...step.assignees.map((assignee) => assignee.userId), ...holders])
    .size;
  return { required, available };
}

/** Where and why a role step cannot collect its approvals. */
export function roleApproverShortfall(
  step: StepInput,
  index: number,
  roleHolders: ApprovalChainFormContext['roleHolders'],
): { path: (string | number)[]; message: string } | null {
  const role = step.approverRole;
  const capacity = roleStepCapacity(step, roleHolders);
  if (!role || !capacity || capacity.available >= capacity.required) return null;
  const { required, available } = capacity;

  const onCount = step.approvalThresholdType === 'at_least_n';
  const message =
    available === 0
      ? i18n.t('approvalChains:manageApprovalChains.schema.stepRoleHasNoApprovers', {
          step: index + 1,
          role: role.name,
        })
      : i18n.t('approvalChains:manageApprovalChains.schema.stepRoleRequiresAtLeastApprovers', {
          step: index + 1,
          required,
          available,
          role: role.name,
        });
  return { path: ['steps', index, onCount ? 'approvalThresholdCount' : 'assignees'], message };
}

/** Resolved form values (post-parse). */
export type ApprovalChainFormData = z.infer<typeof approvalChainFormSchema>;
/** Raw form input values (pre-parse) — what react-hook-form holds. */
export type ApprovalChainFormInput = z.input<typeof approvalChainFormSchema>;

/** A single step as held in form state. */
export type ApprovalChainStepFormData = ApprovalChainFormData['steps'][number];

/** Build an empty step with a fresh client key and sensible defaults. */
export function createEmptyStep(): ApprovalChainStepFormData {
  return {
    tempId: uuidv4(),
    stepId: undefined,
    name: '',
    approvalThresholdType: 'at_least_one',
    approvalThresholdCount: undefined,
    assignees: [],
    approverRole: null,
  };
}

/** Default values for the create flow: one empty step, nothing auto-applied. */
export function createDefaultFormData(): ApprovalChainFormInput {
  return {
    name: '',
    executionMode: 'any_order',
    autoApplyToNewFolders: false,
    autoApplyToExistingFolders: false,
    steps: [createEmptyStep()],
  };
}

/**
 * The generated response DTO types `name`/`email`/`avatarUrl`/count as opaque
 * `{ [key: string]: unknown }` objects. In practice the API returns primitives,
 * so we coerce defensively to the primitive we expect.
 */
function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asApproverRole(
  role: ApprovalChainStep['approverRole'],
): ApprovalChainStepRoleOption | null {
  return role ? { id: role.id, name: role.name } : null;
}

function asExecutionMode(value: unknown): ChainExecutionMode {
  return value === 'any_order' ? 'any_order' : 'in_order';
}

function asOptionalNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return undefined;
}

/**
 * Hydrate an existing approval chain (detail endpoint, `steps` populated) into form
 * state for the edit flow. Assignee display fields come from the response so
 * chips render immediately; the member picker refreshes them once loaded.
 */
export function mapApprovalChainToFormData(approvalChain: ApprovalChain): ApprovalChainFormInput {
  const steps = (approvalChain.steps ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((step) => ({
      tempId: step.id,
      stepId: step.id,
      name: step.name,
      approvalThresholdType: step.approvalThresholdType,
      approvalThresholdCount:
        step.approvalThresholdType === 'at_least_n'
          ? asOptionalNumber(step.approvalThresholdCount)
          : undefined,
      assignees: step.assignees.map((assignee) => ({
        userId: assignee.userId,
        name: asString(assignee.name),
        email: asString(assignee.email),
        avatarUrl: asString(assignee.avatarUrl) || null,
      })),
      approverRole: asApproverRole(step.approverRole),
    }));

  return {
    name: approvalChain.name,
    executionMode: asExecutionMode(approvalChain.executionMode),
    autoApplyToNewFolders: approvalChain.autoApplyToNewFolders,
    // One-time action; always starts unchecked regardless of stored state.
    autoApplyToExistingFolders: false,
    steps: steps.length > 0 ? steps : [createEmptyStep()],
  };
}

/**
 * Hydrate an approval chain copied from another project into form state for the create
 * flow. Mirrors `mapApprovalChainToFormData` but deliberately drops all
 * assignees and resets both auto-apply flags: people may not exist across
 * projects. A step's role is carried over, since the target project has its own
 * holders of it; a step without one needs someone added before the form saves.
 * Structure — name, step names, order, threshold type, and count — is preserved
 * from the source.
 */
export function mapCopiedApprovalChainToFormData(
  approvalChain: ApprovalChain,
): ApprovalChainFormInput {
  const steps = (approvalChain.steps ?? [])
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((step) => ({
      // Fresh client keys — the source ids are meaningless in the new project.
      tempId: uuidv4(),
      stepId: undefined,
      name: step.name,
      approvalThresholdType: step.approvalThresholdType,
      approvalThresholdCount:
        step.approvalThresholdType === 'at_least_n'
          ? asOptionalNumber(step.approvalThresholdCount)
          : undefined,
      // Assignees are intentionally not copied across projects; a role is, since the target
      // project has its own holders of it.
      assignees: [],
      approverRole: asApproverRole(step.approverRole),
    }));

  return {
    name: approvalChain.name,
    executionMode: asExecutionMode(approvalChain.executionMode),
    autoApplyToNewFolders: false,
    autoApplyToExistingFolders: false,
    steps: steps.length > 0 ? steps : [createEmptyStep()],
  };
}

/**
 * Map validated form data to the Update DTO body, which is the Create body plus
 * an optional per-step `id`. Steps hydrated from an existing approval chain carry that
 * id so the server reconciles them in place instead of dropping and recreating
 * them, which is what preserves approvals already collected against a step. New
 * and copied steps have no id and are created. The create flow reuses this
 * mapper: its steps never carry an id, so the body it produces is unchanged.
 */
export function mapFormDataToDto(formData: ApprovalChainFormData): UpdateApprovalChainInput {
  return {
    name: formData.name.trim(),
    executionMode: formData.executionMode,
    autoApplyToNewFolders: formData.autoApplyToNewFolders,
    autoApplyToExistingFolders: formData.autoApplyToExistingFolders,
    steps: formData.steps.map((step) => ({
      id: step.stepId,
      name: step.name.trim(),
      approvalThresholdType: step.approvalThresholdType,
      approvalThresholdCount:
        step.approvalThresholdType === 'at_least_n' ? step.approvalThresholdCount : undefined,
      assigneeUserIds: step.assignees.map((assignee) => assignee.userId),
      approverRoleId: step.approverRole?.id ?? null,
    })),
  };
}
